import styles from './login.module.css';
import { Typography } from '../../../components/typography/typography';
import classNames from 'classnames';
import { Divider } from '../../../components/divider/divider';
import { Field } from '../../../components/field/field';
import { createRef, useEffect, useState } from 'react';
import { SocialLogin } from '../../social-login/social-login';
import { EcommerceButton } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { useCustomerAPI } from '../../../clients/customer-api/customer-api-client';
import { Configuration } from '../../../clients/quipli-service/configuration/configuration';
import { Dictionary, keyBy } from 'lodash';
import { Spinner } from '../../../components/spinner/spinner';
import { useCustomerYorentClient } from '../../../clients/customer-api/customer-yorent-api-client';
import { ecommerceToast } from '../../../util/toasts';
import { Phone } from '../../../components/field/phone';
import { QuipliReCAPTCHARef, QuipliReCAPTCHA } from '../../../components/recaptcha/recaptcha';

type AccountAuthAction = 'Sign In' | 'Sign Up' | 'Guest User' | 'Forgot Password';

export const AccountLogin = ({
  initialAction = 'Sign In',
  redirect,
  isAsync = false,
}: {
  initialAction?: AccountAuthAction;
  redirect: string;
  isAsync?: boolean;
}) => {
  const { getConfigurations } = useCustomerAPI();

  const [action, setAction] = useState<AccountAuthAction>(initialAction);
  const [config, setConfig] = useState<Dictionary<Configuration>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    console.log('test');
    const fetchConfig = async () => {
      setIsLoading(true);
      const response = await getConfigurations([
        'CONF_ENABLE_GUEST_CHECKOUT',
        'CONF_DISABLE_USER_REGISTRATION',
        'CONF_ENABLE_GOOGLEPLUS_LOGIN',
        'CONF_RENTER_PHONE_MANDATORY_FIELD',
        'CONF_GOOGLE_RECAPTCHA_SITE_KEY',
        'CONF_RECAPTCHA_ENABLE',
      ]);
      const _config = keyBy(response, (c) => c.variable);

      if (_config['CONF_DISABLE_USER_REGISTRATION']?.value === '1') {
        setAction('Guest User');
      }

      setConfig(_config);
      setIsLoading(false);
    };

    fetchConfig();
  }, []);

  return (
    <>
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          {(action === 'Sign In' || action === 'Sign Up') && (
            <UserForm action={action} setAction={setAction} config={config} redirect={redirect} isAsync={isAsync} />
          )}

          {action === 'Guest User' && <GuestForm setAction={setAction} config={config} isAsync={isAsync} />}

          {action === 'Forgot Password' && (
            <ForgotPasswordForm setAction={setAction} config={config} isAsync={isAsync} />
          )}
        </>
      )}
    </>
  );
};

const UserForm = ({
  action,
  setAction,
  config,
  redirect,
  isAsync = false,
}: {
  action: AccountAuthAction;
  setAction: (newAction: AccountAuthAction) => void;
  config: Dictionary<Configuration>;
  redirect: string;
  isAsync?: boolean;
}) => {
  const { login, signUp } = useCustomerYorentClient();

  const [name, setName] = useState<string>();
  const [phone, setPhone] = useState<string>();
  const [phoneIsValid, setPhoneIsValid] = useState<boolean>();
  const [email, setEmail] = useState<string>();
  const [password, setPassword] = useState<string>();
  const isDisabled = !email || !password || (action === 'Sign Up' && !name);
  const recaptchaRef = createRef<QuipliReCAPTCHARef>();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (isDisabled) {
      ecommerceToast('Please fill in all the required fields.', 'error');
      return;
    }

    action === 'Sign In' ? handleLogin() : handleSignUp();
  };

  const handleLogin = async () => {
    if (isDisabled) return;

    try {
      const response = await login(email, password, isAsync);

      if (response.status === 1) {
        window.location.reload();
        return;
      } else {
        ecommerceToast(response.msg, 'error', undefined, true);
      }
    } catch (error) {
      ecommerceToast('An error occurred. Please try again.', 'error');
    }
  };

  const handleSignUp = async () => {
    if (isDisabled) return;

    if (config['CONF_RECAPTCHA_ENABLE'].value === '1') {
      if (!recaptchaRef.current) {
        return;
      }

      const validateRecaptcha = recaptchaRef.current.validate();
      if (!validateRecaptcha.isValid) {
        ecommerceToast(validateRecaptcha.error, 'error');
        return;
      }
    }

    if (phone !== '' && !phoneIsValid) {
      ecommerceToast('Please enter a valid phone number', 'error');
      return;
    }

    try {
      const response = await signUp(
        name!,
        email,
        password,
        phone?.toString() || '',
        recaptchaRef.current?.getToken() || '',
        isAsync,
      );

      if (response.status === 1) {
        window.location.reload();
        return;
      } else {
        ecommerceToast(response.msg, 'error', undefined, true);
      }
    } catch (error) {
      ecommerceToast('An error occurred. Please try again.', 'error');
    }
  };

  return config['CONF_DISABLE_USER_REGISTRATION']?.value === '1' ? null : (
    <div className={classNames(styles.loginFormWrapper)}>
      <div className="flex flex-col justify-center">
        <div className="mb-8">
          <Typography type="text-xl" weight="semi-bold" className="text-center">
            {action}
          </Typography>
        </div>

        {config['CONF_ENABLE_GOOGLEPLUS_LOGIN']?.value === '1' && (
          <>
            <SocialLogin redirect={redirect} />

            <Divider text="OR" />
          </>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mt-8 flex flex-col justify-center gap-4">
            {action === 'Sign Up' && (
              <Field
                label="Full Name"
                labelType="text-xs"
                labelWeight="semi-bold"
                type="text"
                value={name}
                onChange={setName}
                isRequired
              />
            )}

            <Field
              label="Email"
              labelType="text-xs"
              labelWeight="semi-bold"
              type="text"
              value={email}
              onChange={setEmail}
              isRequired
            />

            {action === 'Sign Up' && (
              <Phone
                label="Phone Number"
                labelType="text-xs"
                labelWeight="semi-bold"
                isRequired
                value={phone}
                onChange={setPhone}
                onChangeValidity={setPhoneIsValid}
              />
            )}

            <div>
              <Field
                label="Password"
                labelType="text-xs"
                labelWeight="semi-bold"
                type="password"
                value={password}
                onChange={setPassword}
                isRequired
              />

              {action === 'Sign In' ? (
                <div className="cursor-pointer w-fit" onClick={() => setAction('Forgot Password')}>
                  <Typography type="text-xs" className="text-grey-600 hover:text-primary-500 cursor-pointer">
                    Forgot Password?
                  </Typography>
                </div>
              ) : (
                <Typography type="text-sm" className="text-grey-500 flex items-center gap-1 !mt-1">
                  <Icon icon="InfoCircle" size="small" />
                  <span>Use a minimum password length of 6 or more characters.</span>
                </Typography>
              )}
            </div>

            {action === 'Sign Up' &&
              config['CONF_RECAPTCHA_ENABLE'].value === '1' &&
              config['CONF_GOOGLE_RECAPTCHA_SITE_KEY']?.value && (
                <QuipliReCAPTCHA ref={recaptchaRef} sitekey={config['CONF_GOOGLE_RECAPTCHA_SITE_KEY'].value} />
              )}

            <EcommerceButton buttonStyle="primary" type="submit">
              {action}
            </EcommerceButton>

            <Typography type="text-sm">
              {action === 'Sign In' ? (
                <>
                  <span>Don't have an account?</span>{' '}
                  <span
                    className="text-primary-500 hover:underline cursor-pointer"
                    onClick={() => setAction('Sign Up')}
                  >
                    Sign Up
                  </span>{' '}
                  {config['CONF_ENABLE_GUEST_CHECKOUT']?.value === '1' && (
                    <>
                      <span>or</span>{' '}
                      <span
                        className="text-primary-500 hover:underline cursor-pointer"
                        onClick={() => setAction('Guest User')}
                        data-cy="continue_as_guest"
                      >
                        Continue as Guest
                      </span>
                    </>
                  )}
                </>
              ) : (
                <>
                  <span>Already have an account?</span>{' '}
                  <span
                    className="text-primary-500 hover:underline cursor-pointer"
                    onClick={() => setAction('Sign In')}
                  >
                    Sign In
                  </span>
                </>
              )}
            </Typography>
          </div>
        </form>
      </div>
    </div>
  );
};

const GuestForm = ({
  setAction,
  config,
  isAsync = false,
}: {
  setAction: (newAction: AccountAuthAction) => void;
  config: Dictionary<Configuration>;
  isAsync?: boolean;
}) => {
  const { guestLogin } = useCustomerYorentClient();

  const [name, setName] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [phone, setPhone] = useState<string>();
  const recaptchaRef = createRef<QuipliReCAPTCHARef>();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (config['CONF_RECAPTCHA_ENABLE'].value === '1') {
      if (!recaptchaRef.current) {
        return;
      }

      const validateRecaptcha = recaptchaRef.current.validate();
      if (!validateRecaptcha.isValid) {
        ecommerceToast(validateRecaptcha.error, 'error');
        return;
      }
    }

    if (!name || !email || (config['CONF_RENTER_PHONE_MANDATORY_FIELD']?.value === '1' && !phone)) {
      ecommerceToast('Please fill in all the required fields.', 'error');
      return;
    }

    try {
      const response = await guestLogin(name, email, recaptchaRef.current?.getToken() || '', phone, isAsync);

      if (response.status === 1) {
        window.location.reload();
        return;
      } else {
        ecommerceToast(response.msg, 'error', undefined, true);
      }
    } catch (error) {
      ecommerceToast('An error occurred. Please try again.', 'error');
    }
  };

  return (
    <div className={classNames(styles.loginFormWrapper)}>
      <form onSubmit={handleSubmit}>
        <div className={classNames(styles.noSocialButtons)}>
          <Typography type="text-xl" weight="semi-bold" className="text-center">
            Guest User
          </Typography>

          <Field
            label="Full Name"
            labelType="text-xs"
            labelWeight="semi-bold"
            type="text"
            value={name}
            onChange={setName}
            isRequired={true}
            dataCy="guest_name"
          />

          <Field
            label="Email"
            labelType="text-xs"
            labelWeight="semi-bold"
            type="text"
            value={email}
            onChange={setEmail}
            isRequired={true}
            dataCy="guest_email"
          />
          {config['CONF_RENTER_PHONE_MANDATORY_FIELD']?.value === '1' && (
            <Field
              label="Phone Number"
              labelType="text-xs"
              labelWeight="semi-bold"
              type="text"
              value={phone}
              onChange={setPhone}
              isRequired={true}
              dataCy="guest_phone"
            />
          )}

          {config['CONF_RECAPTCHA_ENABLE'].value === '1' && config['CONF_GOOGLE_RECAPTCHA_SITE_KEY']?.value && (
            <QuipliReCAPTCHA ref={recaptchaRef} sitekey={config['CONF_GOOGLE_RECAPTCHA_SITE_KEY'].value} />
          )}

          <EcommerceButton buttonStyle="primary" type="submit" dataCy="continue_as_guest_submit">
            Continue As Guest
          </EcommerceButton>

          {config['CONF_DISABLE_USER_REGISTRATION']?.value === '0' && (
            <Typography type="text-sm">
              <span>Already have an account?</span>{' '}
              <span className="text-primary-500 hover:underline cursor-pointer" onClick={() => setAction('Sign In')}>
                Sign In
              </span>
            </Typography>
          )}
        </div>
      </form>
    </div>
  );
};

const ForgotPasswordForm = ({
  setAction,
  config,
  isAsync = false,
}: {
  setAction: (newAction: AccountAuthAction) => void;
  config: Dictionary<Configuration>;
  isAsync?: boolean;
}) => {
  const { forgotPassword } = useCustomerYorentClient();
  const [email, setEmail] = useState<string>();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (!email) {
      ecommerceToast('Please fill your email address.', 'error');
      return;
    }

    try {
      const response = await forgotPassword(email, isAsync);

      if (response.status === 1) {
        ecommerceToast(response.msg, 'success', undefined, true);
        setAction('Sign In');
        return;
      } else {
        ecommerceToast(response.msg, 'error', undefined, true);
      }
    } catch (error) {
      ecommerceToast('An error occurred. Please try again.', 'error');
    }
  };

  return config['CONF_DISABLE_USER_REGISTRATION']?.value === '1' ? null : (
    <div className={classNames(styles.loginFormWrapper)}>
      <form onSubmit={handleSubmit}>
        <div className={classNames(styles.noSocialButtons)}>
          <Typography type="text-xl" weight="semi-bold" className="text-center">
            Forgot your password?
          </Typography>

          <Typography type="text-sm" className="text-grey-500">
            Enter the email address associated with your account, and we’ll send you a link to reset your password:
          </Typography>

          <Field
            label="Email"
            labelType="text-xs"
            labelWeight="semi-bold"
            type="text"
            value={email}
            onChange={setEmail}
            isRequired
          />

          <EcommerceButton buttonStyle="primary" type="submit">
            Send Reset Link
          </EcommerceButton>

          <Typography type="text-sm" className="text-center">
            <span className="text-primary-500 hover:underline cursor-pointer" onClick={() => setAction('Sign In')}>
              Back to Sign In
            </span>
          </Typography>
        </div>
      </form>
    </div>
  );
};
